import React, { useState, useMemo, useCallback } from "react";
import { useLocation } from "react-use";
import { useTranslation } from "react-i18next";
import styled from "@emotion/styled";
import { Wizard, withIf, withReference } from "@components";
import { WizardsHeader } from "@rentablo/components";
import { useValidation } from "./hooks/useValidation";
import { useTransfer } from "./hooks/useTransfer";
import { SelectTransferTypeStep } from "./components/SelectTransferTypeStep";
import { DepotAutocompleteStep } from "./components/DepotAutocompleteStep";
import { DateOfTransferStep } from "./components/DateOfTransferStep";
import { SelectInvestmentsStep } from "./components/SelectInvestmentsStep";
import { FullLotsStep } from "./components/FullLotsStep";
import { SuccessStep } from "./components/SuccessStep";

const HeaderContainer = styled.div`
  top: 0;
  left: 0;
  right: 0;
  position: absolute;
`;

const NoScroll = styled.div`
  overflow: hidden;
`;

const isAnimationOff = import.meta.env.VITE_APP_WIZARD_ANIMATION_OFF === "true";

type TransferType = "full" | "partial";
type TransferFullLotsType = "onlyFullLots" | "fullLotsWithPartial";

export interface InvestmentToTransfer {
  id: number;
  numberOfLots?: number;
}

export interface Values {
  transferType?: TransferType;
  fromAccountId?: number;
  toAccountId?: number;
  dateOfTransfer?: Date;
  investmentsToTransfer?: InvestmentToTransfer[];
  transferFullLots?: TransferFullLotsType;
}

interface Props {
  fromAccountId: number;
}

const isPartialTransfer = ({ transferType }: Partial<Values>) =>
  transferType === "partial";

const isFullTransfer = ({ transferType }: Partial<Values>) =>
  transferType === "full";

export const DepotTransferWizard = ({ fromAccountId }: Props) => {
  const { t } = useTranslation();
  const location = useLocation();
  const { validate, isFetching } = useValidation(fromAccountId);
  const transfer = useTransfer();
  const [finished, setFinished] = useState(false);
  const [error, setError] = useState<string>();

  const handleFinish = useCallback(
    async (values: Values) => {
      try {
        await transfer(values);
        setError(undefined);
        setFinished(true);
      } catch (e) {
        // TODO: Find a solution for types
        // @ts-ignore
        setError(e.response.data.message);
      }
    },
    [transfer]
  );

  const wizardChildren = useMemo(
    () => [
      withReference<Values, "transferType">(
        "transferType",
        SelectTransferTypeStep
      ),
      withIf<Values>(isPartialTransfer, [
        withReference<Partial<Values>, "investmentsToTransfer">(
          "investmentsToTransfer",
          SelectInvestmentsStep
        ),
      ]),
      withReference<Values, "toAccountId">(
        "toAccountId",
        DepotAutocompleteStep
      ),
      withIf<Values>(isFullTransfer, [
        withReference<Values, "transferFullLots">(
          "transferFullLots",
          FullLotsStep
        ),
      ]),
      withReference<Values, "dateOfTransfer">(
        "dateOfTransfer",
        (props: any) => <DateOfTransferStep {...props} errorMessage={error} />
      ),
    ],
    [error]
  );

  const redirectUrl =
    (location.search &&
      new URLSearchParams(location.search).get("redirectUrl")) ||
    "/";

  return (
    <>
      <title>{t("depotTransfer.title")}</title>

      <NoScroll data-testid="depot-transfer-wizard">
        {!finished && (
          <Wizard<Values>
            loading={isFetching}
            initialData={{ fromAccountId }}
            animation={!isAnimationOff}
            onFinish={handleFinish}
            validation={validate}
            children={wizardChildren}
          />
        )}
        {finished && <SuccessStep backUrl={`/depots/${fromAccountId}`} />}
        <HeaderContainer>
          <WizardsHeader
            backUrl={redirectUrl}
            title={t("depotTransfer.header.title")}
          />
        </HeaderContainer>
      </NoScroll>
    </>
  );
};
